import {
  Row,
  CellMeta,
  TableKeys,
  PortCoKeys,
  HeaderKeys,
  ExpansionKeys,
  SeparatorKeys
} from '../types';

export const COMMA_OPTIONS = {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
};

export const INT_KEYS: Partial<PortCoKeys>[] = [
  PortCoKeys.CUSTOMERS_EOP,
  PortCoKeys.CUSTOMERS_GROSSNEW,
  PortCoKeys.EMPLOYEES_SM,
  PortCoKeys.EMPLOYEES_RD,
  PortCoKeys.EMPLOYEES_GA,
  PortCoKeys.EMPLOYEES_TOTAL,
  PortCoKeys.EMPLOYEES_OTHER
];

export const INT_TABLE_KEYS: Partial<TableKeys>[] = [
  TableKeys.TOTAL_CUSTOMERS,
  TableKeys.GROSS_NEW_CUSTOMERS_IN_PERIOD,
  TableKeys.TOTAL_HEADCOUNT_SM,
  TableKeys.TOTAL_HEADCOUNT_RD,
  TableKeys.TOTAL_HEADCOUNT_GA,
  TableKeys.TOTAL_HEADCOUNT,
  TableKeys.TOTAL_HEADCOUNT_OTHER
];

export const MIL_KEYS: Partial<PortCoKeys>[] = [
  PortCoKeys.ARR_BOP,
  PortCoKeys.ARR_NEWLOGO,
  PortCoKeys.ARR_EXPANSION,
  PortCoKeys.ARR_GROSSNEW,
  PortCoKeys.ARR_CHURN_GROSS,
  PortCoKeys.ARR_NETNEW,
  PortCoKeys.ARR_EOP,
  PortCoKeys.REVENUE_TOTAL,
  PortCoKeys.GROSSPROFIT_TOTAL,
  PortCoKeys.OPEX_GA,
  PortCoKeys.OPEX_RD,
  PortCoKeys.OPEX_SM,
  PortCoKeys.OPEX_TOTAL,
  PortCoKeys.FCF,
  PortCoKeys.ANNUALIZED_REVENUE
];

export const THOUSANDS_KEYS: Partial<PortCoKeys>[] = [
  PortCoKeys.ARR_PER_CUSTOMER,
  PortCoKeys.ARR_PER_FTE,
  PortCoKeys.OPEX_PER_FTE
];

export const PERC_KEYS: Partial<PortCoKeys>[] = [
  PortCoKeys.YOY_ARR_GROWTH,
  PortCoKeys.ARR_PERCTOTAL_GROSSNEW_NEWLOGO,
  PortCoKeys.ARR_PERCTOTAL_GROSSNEW_EXPANSION,
  PortCoKeys.NEW_LOGO_VELOCITY,
  PortCoKeys.RETENTION_GROSS_ARR_ANNUALIZED,
  PortCoKeys.RETENTION_NET_ARR_ANNUALIZED,
  PortCoKeys.RULE_OF_40,
  PortCoKeys.MARGIN_OPEX_GA,
  PortCoKeys.MARGIN_OPEX_RD,
  PortCoKeys.MARGIN_OPEX_SM,
  PortCoKeys.MARGIN_FCF,
  PortCoKeys.MARGIN_GROSSPROFIT,
  PortCoKeys['MARGIN_FCF.1'],
  PortCoKeys['MARGIN_GROSSPROFIT.1'],
  PortCoKeys.EMPLOYEES_PERCTOTAL_GA,
  PortCoKeys.EMPLOYEES_PERCTOTAL_RD,
  PortCoKeys.EMPLOYEES_PERCTOTAL_SM,
  PortCoKeys.EMPLOYEES_PERCTOTAL_OTHER
];

export const COMMA_KEYS: Partial<PortCoKeys>[] = [
  PortCoKeys.CUSTOMERS_EOP,
  PortCoKeys.CAC
];

export const COMMA_TABLE_KEYS: Partial<TableKeys>[] = [
  TableKeys.TOTAL_CUSTOMERS,
  TableKeys.CAC
];

export const ENDING_ARR_ALL_DEPENDENCIES: TableKeys[] = [
  TableKeys.BOP_ARR,
  TableKeys.ENDING_ARR,
  TableKeys.NET_NEW_ARR,
  TableKeys.NEW_LOGO_ARR,
  TableKeys.GROSS_NEW_ARR,
  TableKeys.EXPANSION_ARR,
  TableKeys.GROSS_CHURN_ARR
];

const TABLE_TITLES: Record<TableKeys, string> = {
  // KEY FINANCIAL SECTIONS
  [TableKeys.BOP_ARR]: 'Beg. of Period ARR ($M)',
  [TableKeys.NEW_LOGO_ARR]: 'New Logo ARR ($M) (Optional)',
  [TableKeys.EXPANSION_ARR]: 'Expansion ARR ($M) (Optional)',
  [TableKeys.GROSS_NEW_ARR]: 'Gross New ARR ($M) (Optional)',
  [TableKeys.GROSS_CHURN_ARR]: 'Gross Churn ARR ($M) (Optional)',
  [TableKeys.NET_NEW_ARR]: 'Net New ARR ($M)',
  [TableKeys.ENDING_ARR]: 'Ending ARR ($M)',
  [TableKeys.REVENUE]: 'Revenue ($M)',
  [TableKeys.ANNUALIZED_REVENUE]: 'Annualized Revenue or ARR ($M)',
  [TableKeys.TOTAL_GROSS_PROFIT]: 'Total Gross Profit ($M)',
  [TableKeys.GROSS_PROFIT_MARGIN]: 'Gross Profit Margin (%)',
  [TableKeys.TOTAL_OPEX_SM]: 'S&M OpEx ($M)',
  [TableKeys.TOTAL_OPEX_RD]: 'R&D OpEx ($M)',
  [TableKeys.TOTAL_OPEX_GA]: 'G&A OpEx ($M)',
  [TableKeys.TOTAL_OPEX]: 'Total OpEx ($M)',
  [TableKeys.FCF]: 'FCF ($M)',
  [TableKeys.FCF_MARGIN]: 'FCF Margin (%)',
  [TableKeys.TOTAL_CUSTOMERS]: 'Total Customers',
  [TableKeys.GROSS_NEW_CUSTOMERS_IN_PERIOD]: 'Gross New Customers in Period',
  [TableKeys.ARR_PER_CUSTOMER]: "ARR Per Customer (K's $)",
  [TableKeys.TOTAL_HEADCOUNT_SM]: 'Total S&M Headcount',
  [TableKeys.TOTAL_HEADCOUNT_RD]: 'Total R&D Headcount',
  [TableKeys.TOTAL_HEADCOUNT_GA]: 'Total G&A Headcount',
  [TableKeys.TOTAL_HEADCOUNT]: 'Total Headcount',
  [TableKeys.TOTAL_HEADCOUNT_OTHER]: 'Total Other Headcount',

  // GROWTH AND RETENTION
  [TableKeys.YOY_ARR_GROWTH]: 'YoY ARR Growth (%)',
  [TableKeys.COMPOSITION_NEW_LOGO]: 'New Logo (%)',
  [TableKeys.COMPOSITION_EXPANSION]: 'Expansion (%)',
  [TableKeys.NEW_LOGO_VELOCITY]: 'YoY New Logo Growth (%)',
  [TableKeys.QUICK_RATIO]: 'Quick Ratio (x)',
  [TableKeys.GROSS_DOLLAR_RETENTION]: 'Gross Dollar Retention (%)',
  [TableKeys.NET_DOLLAR_RETENTION]: 'Net Dollar Retention (%)',

  // OPERATIONAL EFFICIENCY
  [TableKeys.NET_MAGIC_NUMBER]: 'Net Magic Number (x)',
  [TableKeys.RULE_OF_40]: 'Rule of 40 (%)',
  [TableKeys.CAC]: 'CAC',
  [TableKeys.CAC_PAYBACK_PERIOD]: 'CAC Payback Period (Months)',
  [TableKeys.BURN_MULTIPLE]: 'Burn Multiple (x)',
  [TableKeys.PERC_OPEX_SM]: 'S&M OpEx as % of Revenue (%)',
  [TableKeys.PERC_OPEX_RD]: 'R&D OpEx as % of Revenue (%)',
  [TableKeys.PERC_OPEX_GA]: 'G&A OpEx as % of Revenue (%)',

  // PROFITABILITY AND CASH FLOW
  [TableKeys['GROSS_PROFIT_MARGIN_1']]: 'Gross Profit Margin (%)',
  [TableKeys['FCF_MARGIN_1']]: 'FCF Margin (%)',

  // HEADCOUNT
  [TableKeys.ARR_PER_FTE]: "ARR Per FTE (K's $)",
  [TableKeys.OPEX_PER_FTE]: "OpEx Per FTE (K's $)",
  [TableKeys.PERC_HEADCOUNT_SM]: 'S&M FTE Total (%)',
  [TableKeys.PERC_HEADCOUNT_RD]: 'R&D FTE Total (%)',
  [TableKeys.PERC_HEADCOUNT_GA]: 'G&A FTE Total (%)',
  [TableKeys.PERC_HEADCOUNT_OTHER]: 'Other FTE Total (%)'
};

export const EXPANSION_TITLES: Record<ExpansionKeys, string> = {
  [ExpansionKeys.EXPANSION_ARR_FUNNEL]:
    'Optional ARR Fields (New Logo, Expansion, Gross New and Gross Churn)'
};

export const EXPANSION_TITLES_EXPANDED: Record<ExpansionKeys, string> = {
  [ExpansionKeys.EXPANSION_ARR_FUNNEL]: 'Optional ARR Fields'
};

export const HEADER_TITLES: Record<HeaderKeys, string> = {
  [HeaderKeys.HEADER_OPEX_DETAILS]: 'OPEX DETAILS',
  [HeaderKeys.HEADER_HEADCOUNT_DETAILS]: 'HEADCOUNT DETAILS',
  [HeaderKeys.HEADER_GROSS_NEW_ARR_COMPOSITION]: 'GROSS NEW ARR COMPOSITION',
  [HeaderKeys.HEADER_OPEX_AS_PERC_OF_REVENUE]: 'OPEX AS % OF REVENUE',
  [HeaderKeys.HEADER_HEADCOUNT_DISTRIBUTION]: 'HEADCOUNT DISTRIBUTION'
};

const FORMULAS: Partial<Record<TableKeys, string>> = {
  [TableKeys.GROSS_NEW_ARR]: 'New Logo ARR + Expansion ARR',
  [TableKeys.NET_NEW_ARR]: 'Gross New ARR + Gross Churn ARR',
  [TableKeys.ENDING_ARR]: 'Beg. of Period ARR + Net New ARR',
  [TableKeys.ANNUALIZED_REVENUE]: 'Ending ARR or (Revenue * 4)',
  [TableKeys.GROSS_PROFIT_MARGIN]: '(Total Gross Profit / Revenue) * 100',
  [TableKeys.TOTAL_OPEX]: 'Total S&M OpEx + Total R&D OpEx + Total G&A OpEx',
  [TableKeys.FCF_MARGIN]: '(FCF / Revenue) * 100',
  [TableKeys.ARR_PER_CUSTOMER]:
    '([(Annualized Revenue or Revenue) * 10^6] / Total Customers) / 10^3',
  [TableKeys.TOTAL_HEADCOUNT]:
    'Total S&M Headcount + Total R&D Headcount + Total G&A Headcount',

  // GROWTH AND RETENTION
  [TableKeys.YOY_ARR_GROWTH]:
    "[(Ending ARR / Last Year's Ending ARR ) -1] * 100",
  [TableKeys.COMPOSITION_NEW_LOGO]: '(New Logo ARR / Gross New ARR) * 100',
  [TableKeys.COMPOSITION_EXPANSION]: '(Expansion ARR / Gross New ARR) * 100',
  [TableKeys.NEW_LOGO_VELOCITY]:
    "[(New Logo ARR / Last Year's New Logo ARR) -1] * 100",
  [TableKeys.QUICK_RATIO]: 'Gross New ARR / ABS(Gross Churn ARR)',
  [TableKeys.GROSS_DOLLAR_RETENTION]:
    '(1 + [(Gross Churn ARR * 4) / ((Beg. of Period ARR + Ending ARR) / 2)]) * 100',
  [TableKeys.NET_DOLLAR_RETENTION]:
    '(1 + [((Net New ARR - New Logo ARR) * 4) / ((Beg. of Period ARR + Ending ARR) / 2)]) * 100',

  // OPERATIONAL EFFICIENCY
  [TableKeys.NET_MAGIC_NUMBER]: "Net New ARR / Last Quarter's Total S&M OpEx",
  [TableKeys.RULE_OF_40]: 'FCF Margin + YoY ARR Growth',
  [TableKeys.CAC]: '(Total S&M OpEx * 10^6) / Gross New Customers in Period',
  [TableKeys.CAC_PAYBACK_PERIOD]:
    'CAC / [(ARR Per Customer * 10^3) * (Gross Profit Marign / 100)]',
  [TableKeys.BURN_MULTIPLE]: 'ABS(FCF) / Net New ARR',
  [TableKeys.PERC_OPEX_SM]: '(Total S&M OpEx / Revenue) * 100',
  [TableKeys.PERC_OPEX_RD]: '(Total R&D OpEx / Revenue) * 100',
  [TableKeys.PERC_OPEX_GA]: '(Total G&A OpEx / Revenue) * 100',

  // PROFITABILITY AND CASH FLOW
  [TableKeys['GROSS_PROFIT_MARGIN_1']]: '(Total Gross Profit / Revenue) * 100',
  [TableKeys['FCF_MARGIN_1']]: '(FCF / Revenue) * 100',

  // HEADCOUNT
  [TableKeys.ARR_PER_FTE]:
    '([(Annualized Revenue or Revenue) * 10^6] / Total Headcount) / 10^3',
  [TableKeys.OPEX_PER_FTE]:
    '[(Total OpEx * 10^6 * 4) / Total Headcount] / 10^3',
  [TableKeys.PERC_HEADCOUNT_SM]:
    '(Total S&M Headcount / Total Headcount) * 100',
  [TableKeys.PERC_HEADCOUNT_RD]:
    '(Total R&D Headcount / Total Headcount) * 100',
  [TableKeys.PERC_HEADCOUNT_GA]:
    '(Total G&A Headcount / Total Headcount) * 100',
  [TableKeys.PERC_HEADCOUNT_OTHER]:
    '(Total Other Headcount / Total Headcount) * 100'
};

const META_DATA: Record<PortCoKeys, CellMeta> = {
  [PortCoKeys.ARR_BOP]: { allowDecimal: true, isIndentedTitle: true },
  [PortCoKeys.EXPANSION_ARR_FUNNEL]: {
    isAccordion: true,
    accordionChildrenCount: 4
  },
  [PortCoKeys.ARR_NEWLOGO]: { allowDecimal: true, isIndentedTitle: true },
  [PortCoKeys.ARR_EXPANSION]: {
    allowNegative: true,
    allowDecimal: true,
    isIndentedTitle: true
  },
  [PortCoKeys.ARR_GROSSNEW]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.GROSS_NEW_ARR],
    isIndentedTitle: true
  },
  [PortCoKeys.ARR_CHURN_GROSS]: {
    isNegative: true,
    allowDecimal: true,
    isIndentedTitle: true
  },
  [PortCoKeys.ARR_NETNEW]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.NET_NEW_ARR],
    isIndentedTitle: true
  },
  [PortCoKeys.ARR_EOP]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.ENDING_ARR],
    isBoldTitle: true
  },
  [PortCoKeys.REVENUE_TOTAL]: { allowDecimal: true, isBoldTitle: true },
  [PortCoKeys.ANNUALIZED_REVENUE]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.ANNUALIZED_REVENUE],
    allowEdit: false,
    isBoldTitle: true
  },
  [PortCoKeys.SEPARATOR_1]: { isSeparator: true },
  [PortCoKeys.GROSSPROFIT_TOTAL]: { allowDecimal: true },
  [PortCoKeys.MARGIN_GROSSPROFIT]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.GROSS_PROFIT_MARGIN],
    isItalic: true,
    allowEdit: false,
    isIndentedTitle: true,
    isBoldTitle: true
  },
  [PortCoKeys.HEADER_OPEX_DETAILS]: { isHeader: true },
  [PortCoKeys.OPEX_SM]: {
    allowDecimal: true,
    isIndentedTitle: true,
    isBoldTitle: true
  },
  [PortCoKeys.OPEX_RD]: {
    allowDecimal: true,
    isIndentedTitle: true,
    isBoldTitle: true
  },
  [PortCoKeys.OPEX_GA]: {
    allowDecimal: true,
    isIndentedTitle: true,
    isBoldTitle: true
  },
  [PortCoKeys.OPEX_TOTAL]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.TOTAL_OPEX],
    isBoldTitle: true
  },
  [PortCoKeys.SEPARATOR_2]: { isSeparator: true },
  [PortCoKeys.FCF]: { allowNegative: true, allowDecimal: true },
  [PortCoKeys.MARGIN_FCF]: {
    allowDecimal: true,
    allowNegative: true,
    formula: FORMULAS[TableKeys.FCF_MARGIN],
    isItalic: true,
    allowEdit: false,
    isIndentedTitle: true,
    isBoldTitle: true
  },
  [PortCoKeys.SEPARATOR_3]: { isSeparator: true },
  [PortCoKeys.CUSTOMERS_EOP]: {},
  [PortCoKeys.CUSTOMERS_GROSSNEW]: { isIndentedTitle: true },
  [PortCoKeys.ARR_PER_CUSTOMER]: {
    allowEdit: false,
    allowDecimal: true,
    formula: FORMULAS[TableKeys.ARR_PER_CUSTOMER],
    isBoldTitle: true
  },
  [PortCoKeys.SEPARATOR_4]: { isSeparator: true },
  [PortCoKeys.HEADER_HEADCOUNT_DETAILS]: { isHeader: true },
  [PortCoKeys.EMPLOYEES_SM]: { isIndentedTitle: true },
  [PortCoKeys.EMPLOYEES_RD]: { isIndentedTitle: true },
  [PortCoKeys.EMPLOYEES_GA]: { isIndentedTitle: true },
  [PortCoKeys.EMPLOYEES_TOTAL]: {
    formula: FORMULAS[TableKeys.TOTAL_HEADCOUNT],
    isBoldTitle: true
  },
  [PortCoKeys.EMPLOYEES_OTHER]: { isBoldTitle: true },
  [PortCoKeys.YOY_ARR_GROWTH]: {
    allowDecimal: true,
    allowNegative: true,
    formula: FORMULAS[TableKeys.YOY_ARR_GROWTH],
    allowEdit: false,
    isItalic: true,
    isBoldTitle: true
  },
  [PortCoKeys.HEADER_GROSS_NEW_ARR_COMPOSITION]: { isHeader: true },
  [PortCoKeys.ARR_PERCTOTAL_GROSSNEW_NEWLOGO]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.COMPOSITION_NEW_LOGO],
    allowEdit: false,
    isItalic: true,
    isIndentedTitle: true,
    isBoldTitle: true
  },
  [PortCoKeys.ARR_PERCTOTAL_GROSSNEW_EXPANSION]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.COMPOSITION_EXPANSION],
    allowEdit: false,
    isItalic: true,
    isIndentedTitle: true,
    isBoldTitle: true
  },
  [PortCoKeys.NEW_LOGO_VELOCITY]: {
    allowDecimal: true,
    allowNegative: true,
    formula: FORMULAS[TableKeys.NEW_LOGO_VELOCITY],
    allowEdit: false,
    isItalic: true,
    isBoldTitle: true
  },
  [PortCoKeys.QUICK_RATIO_ARR]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.QUICK_RATIO],
    allowEdit: false,
    isBoldTitle: true
  },
  [PortCoKeys.RETENTION_GROSS_ARR_ANNUALIZED]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.GROSS_DOLLAR_RETENTION],
    allowEdit: false,
    isItalic: true,
    isBoldTitle: true
  },
  [PortCoKeys.RETENTION_NET_ARR_ANNUALIZED]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.NET_DOLLAR_RETENTION],
    allowEdit: false,
    isItalic: true,
    isBoldTitle: true
  },
  [PortCoKeys.MAGICNUMBER_NET]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.NET_MAGIC_NUMBER],
    allowEdit: false,
    isBoldTitle: true
  },
  [PortCoKeys.RULE_OF_40]: {
    allowDecimal: true,
    allowNegative: true,
    formula: FORMULAS[TableKeys.RULE_OF_40],
    allowEdit: false,
    isItalic: true,
    isBoldTitle: true
  },
  [PortCoKeys.CAC]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.CAC],
    allowEdit: false,
    isBoldTitle: true
  },
  [PortCoKeys.CAC_PAYBACK]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.CAC_PAYBACK_PERIOD],
    allowEdit: false,
    isBoldTitle: true
  },
  [PortCoKeys.BURN_MULTIPLE]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.BURN_MULTIPLE],
    allowEdit: false,
    isBoldTitle: true
  },
  [PortCoKeys.HEADER_OPEX_AS_PERC_OF_REVENUE]: { isHeader: true },
  [PortCoKeys.MARGIN_OPEX_SM]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.PERC_OPEX_SM],
    allowEdit: false,
    isItalic: true,
    isIndentedTitle: true
  },
  [PortCoKeys.MARGIN_OPEX_RD]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.PERC_OPEX_RD],
    allowEdit: false,
    isItalic: true,
    isIndentedTitle: true
  },
  [PortCoKeys.MARGIN_OPEX_GA]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.PERC_OPEX_GA],
    allowEdit: false,
    isItalic: true,
    isIndentedTitle: true
  },
  [PortCoKeys['MARGIN_GROSSPROFIT.1']]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.GROSS_PROFIT_MARGIN_1],
    allowEdit: false,
    isItalic: true,
    isIndentedTitle: true,
    isBoldTitle: true
  },
  [PortCoKeys['MARGIN_FCF.1']]: {
    allowDecimal: true,
    allowNegative: true,
    formula: FORMULAS[TableKeys.FCF_MARGIN_1],
    allowEdit: false,
    isItalic: true,
    isIndentedTitle: true,
    isBoldTitle: true
  },
  [PortCoKeys.ARR_PER_FTE]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.ARR_PER_FTE],
    allowEdit: false,
    isBoldTitle: true
  },
  [PortCoKeys.OPEX_PER_FTE]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.OPEX_PER_FTE],
    allowEdit: false,
    isBoldTitle: true
  },
  [PortCoKeys.HEADER_HEADCOUNT_DISTRIBUTION]: { isHeader: true },
  [PortCoKeys.EMPLOYEES_PERCTOTAL_SM]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.PERC_HEADCOUNT_SM],
    allowEdit: false,
    isIndentedTitle: true,
    isBoldTitle: true
  },
  [PortCoKeys.EMPLOYEES_PERCTOTAL_RD]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.PERC_HEADCOUNT_RD],
    allowEdit: false,
    isIndentedTitle: true,
    isBoldTitle: true
  },
  [PortCoKeys.EMPLOYEES_PERCTOTAL_GA]: {
    allowDecimal: true,
    formula: FORMULAS[TableKeys.PERC_HEADCOUNT_GA],
    allowEdit: false,
    isIndentedTitle: true,
    isBoldTitle: true
  },
  [PortCoKeys.EMPLOYEES_PERCTOTAL_OTHER]: {
    formula: FORMULAS[TableKeys.PERC_HEADCOUNT_OTHER],
    allowDecimal: true,
    allowEdit: false,
    isIndentedTitle: true,
    isBoldTitle: true
  }
};

export const DEPENDENCIES: Record<TableKeys, TableKeys[]> = {
  // KEY FINANCIAL SECTIONS
  [TableKeys.BOP_ARR]: [],
  [TableKeys.NEW_LOGO_ARR]: [],
  [TableKeys.EXPANSION_ARR]: [],
  [TableKeys.GROSS_NEW_ARR]: [TableKeys.NEW_LOGO_ARR, TableKeys.EXPANSION_ARR],
  [TableKeys.GROSS_CHURN_ARR]: [],
  [TableKeys.NET_NEW_ARR]: [TableKeys.GROSS_NEW_ARR, TableKeys.GROSS_CHURN_ARR],
  [TableKeys.ENDING_ARR]: [TableKeys.BOP_ARR, TableKeys.NET_NEW_ARR],
  [TableKeys.REVENUE]: [],
  [TableKeys.ANNUALIZED_REVENUE]: [TableKeys.ENDING_ARR, TableKeys.REVENUE],
  [TableKeys.TOTAL_GROSS_PROFIT]: [],
  [TableKeys.GROSS_PROFIT_MARGIN]: [
    TableKeys.TOTAL_GROSS_PROFIT,
    TableKeys.REVENUE
  ],
  [TableKeys.TOTAL_OPEX_SM]: [],
  [TableKeys.TOTAL_OPEX_RD]: [],
  [TableKeys.TOTAL_OPEX_GA]: [],
  [TableKeys.TOTAL_OPEX]: [
    TableKeys.TOTAL_OPEX_SM,
    TableKeys.TOTAL_OPEX_RD,
    TableKeys.TOTAL_OPEX_GA
  ],
  [TableKeys.FCF]: [],
  [TableKeys.FCF_MARGIN]: [TableKeys.FCF, TableKeys.REVENUE],
  [TableKeys.TOTAL_CUSTOMERS]: [],
  [TableKeys.GROSS_NEW_CUSTOMERS_IN_PERIOD]: [],
  [TableKeys.ARR_PER_CUSTOMER]: [
    TableKeys.ANNUALIZED_REVENUE,
    TableKeys.REVENUE,
    TableKeys.TOTAL_CUSTOMERS
  ],
  [TableKeys.TOTAL_HEADCOUNT_SM]: [],
  [TableKeys.TOTAL_HEADCOUNT_RD]: [],
  [TableKeys.TOTAL_HEADCOUNT_GA]: [],
  [TableKeys.TOTAL_HEADCOUNT]: [
    TableKeys.TOTAL_HEADCOUNT_SM,
    TableKeys.TOTAL_HEADCOUNT_RD,
    TableKeys.TOTAL_HEADCOUNT_GA
  ],
  [TableKeys.TOTAL_HEADCOUNT_OTHER]: [],

  // GROWTH AND RETENTION
  [TableKeys.YOY_ARR_GROWTH]: [TableKeys.ENDING_ARR],
  [TableKeys.COMPOSITION_NEW_LOGO]: [
    TableKeys.NEW_LOGO_ARR,
    TableKeys.GROSS_NEW_ARR
  ],
  [TableKeys.COMPOSITION_EXPANSION]: [
    TableKeys.EXPANSION_ARR,
    TableKeys.GROSS_NEW_ARR
  ],
  [TableKeys.NEW_LOGO_VELOCITY]: [TableKeys.NEW_LOGO_ARR],
  [TableKeys.QUICK_RATIO]: [TableKeys.GROSS_NEW_ARR, TableKeys.GROSS_CHURN_ARR],
  [TableKeys.GROSS_DOLLAR_RETENTION]: [
    TableKeys.GROSS_CHURN_ARR,
    TableKeys.ENDING_ARR,
    TableKeys.BOP_ARR
  ],
  [TableKeys.NET_DOLLAR_RETENTION]: [
    TableKeys.NET_NEW_ARR,
    TableKeys.NEW_LOGO_ARR,
    TableKeys.ENDING_ARR,
    TableKeys.BOP_ARR
  ],

  // OPERATIONAL EFFICIENCY
  [TableKeys.NET_MAGIC_NUMBER]: [TableKeys.NET_NEW_ARR],
  [TableKeys.RULE_OF_40]: [TableKeys.FCF_MARGIN, TableKeys.YOY_ARR_GROWTH],
  [TableKeys.CAC]: [
    TableKeys.TOTAL_OPEX_SM,
    TableKeys.GROSS_NEW_CUSTOMERS_IN_PERIOD
  ],
  [TableKeys.CAC_PAYBACK_PERIOD]: [
    TableKeys.CAC,
    TableKeys.ARR_PER_CUSTOMER,
    TableKeys.GROSS_PROFIT_MARGIN
  ],
  [TableKeys.BURN_MULTIPLE]: [TableKeys.FCF, TableKeys.NET_NEW_ARR],
  [TableKeys.PERC_OPEX_SM]: [TableKeys.TOTAL_OPEX_SM, TableKeys.REVENUE],
  [TableKeys.PERC_OPEX_RD]: [TableKeys.TOTAL_OPEX_RD, TableKeys.REVENUE],
  [TableKeys.PERC_OPEX_GA]: [TableKeys.TOTAL_OPEX_GA, TableKeys.REVENUE],

  // PROFITABILITY AND CASH FLOW
  [TableKeys.GROSS_PROFIT_MARGIN_1]: [
    TableKeys.TOTAL_GROSS_PROFIT,
    TableKeys.REVENUE
  ],
  [TableKeys.FCF_MARGIN_1]: [TableKeys.FCF, TableKeys.REVENUE],

  // HEADCOUNT
  [TableKeys.ARR_PER_FTE]: [
    TableKeys.ANNUALIZED_REVENUE,
    TableKeys.REVENUE,
    TableKeys.TOTAL_HEADCOUNT
  ],
  [TableKeys.OPEX_PER_FTE]: [TableKeys.TOTAL_OPEX, TableKeys.TOTAL_HEADCOUNT],
  [TableKeys.PERC_HEADCOUNT_SM]: [
    TableKeys.TOTAL_HEADCOUNT_SM,
    TableKeys.TOTAL_HEADCOUNT
  ],
  [TableKeys.PERC_HEADCOUNT_RD]: [
    TableKeys.TOTAL_HEADCOUNT_RD,
    TableKeys.TOTAL_HEADCOUNT
  ],
  [TableKeys.PERC_HEADCOUNT_GA]: [
    TableKeys.TOTAL_HEADCOUNT_GA,
    TableKeys.TOTAL_HEADCOUNT
  ],
  [TableKeys.PERC_HEADCOUNT_OTHER]: [
    TableKeys.TOTAL_HEADCOUNT_OTHER,
    TableKeys.TOTAL_HEADCOUNT
  ]
};

export const INITIAL_PORTCO_DATA: Record<PortCoKeys, Row> = {
  // ------------------------ KEY FINANCIAL SECTIONS ------------------------ //
  [PortCoKeys.ARR_BOP]: {
    id: TableKeys.BOP_ARR,
    title: TABLE_TITLES[TableKeys.BOP_ARR],
    meta: META_DATA[PortCoKeys.ARR_BOP]
  },
  [PortCoKeys.EXPANSION_ARR_FUNNEL]: {
    id: ExpansionKeys.EXPANSION_ARR_FUNNEL,
    title: EXPANSION_TITLES[ExpansionKeys.EXPANSION_ARR_FUNNEL],
    titleExpanded:
      EXPANSION_TITLES_EXPANDED[ExpansionKeys.EXPANSION_ARR_FUNNEL],
    meta: META_DATA[PortCoKeys.EXPANSION_ARR_FUNNEL]
  },
  [PortCoKeys.ARR_NEWLOGO]: {
    id: TableKeys.NEW_LOGO_ARR,
    title: TABLE_TITLES[TableKeys.NEW_LOGO_ARR],
    meta: META_DATA[PortCoKeys.ARR_NEWLOGO]
  },
  [PortCoKeys.ARR_EXPANSION]: {
    id: TableKeys.EXPANSION_ARR,
    title: TABLE_TITLES[TableKeys.EXPANSION_ARR],
    meta: META_DATA[PortCoKeys.ARR_EXPANSION]
  },
  [PortCoKeys.ARR_GROSSNEW]: {
    id: TableKeys.GROSS_NEW_ARR,
    title: TABLE_TITLES[TableKeys.GROSS_NEW_ARR],
    meta: META_DATA[PortCoKeys.ARR_GROSSNEW]
  },
  [PortCoKeys.ARR_CHURN_GROSS]: {
    id: TableKeys.GROSS_CHURN_ARR,
    title: TABLE_TITLES[TableKeys.GROSS_CHURN_ARR],
    meta: META_DATA[PortCoKeys.ARR_CHURN_GROSS]
  },
  [PortCoKeys.ARR_NETNEW]: {
    id: TableKeys.NET_NEW_ARR,
    title: TABLE_TITLES[TableKeys.NET_NEW_ARR],
    meta: META_DATA[PortCoKeys.ARR_NETNEW]
  },
  [PortCoKeys.ARR_EOP]: {
    id: TableKeys.ENDING_ARR,
    title: TABLE_TITLES[TableKeys.ENDING_ARR],
    meta: META_DATA[PortCoKeys.ARR_EOP]
  },
  [PortCoKeys.REVENUE_TOTAL]: {
    id: TableKeys.REVENUE,
    title: TABLE_TITLES[TableKeys.REVENUE],
    meta: META_DATA[PortCoKeys.REVENUE_TOTAL]
  },
  [PortCoKeys.ANNUALIZED_REVENUE]: {
    id: TableKeys.ANNUALIZED_REVENUE,
    title: TABLE_TITLES[TableKeys.ANNUALIZED_REVENUE],
    meta: META_DATA[PortCoKeys.ANNUALIZED_REVENUE]
  },
  [PortCoKeys.SEPARATOR_1]: {
    id: SeparatorKeys.SEPARATOR_1,
    title: '',
    meta: META_DATA[PortCoKeys.SEPARATOR_1]
  },
  [PortCoKeys.GROSSPROFIT_TOTAL]: {
    id: TableKeys.TOTAL_GROSS_PROFIT,
    title: TABLE_TITLES[TableKeys.TOTAL_GROSS_PROFIT],
    meta: META_DATA[PortCoKeys.GROSSPROFIT_TOTAL]
  },
  [PortCoKeys.MARGIN_GROSSPROFIT]: {
    id: TableKeys.GROSS_PROFIT_MARGIN,
    title: TABLE_TITLES[TableKeys.GROSS_PROFIT_MARGIN],
    meta: META_DATA[PortCoKeys.MARGIN_GROSSPROFIT]
  },
  [PortCoKeys.HEADER_OPEX_DETAILS]: {
    id: HeaderKeys.HEADER_OPEX_DETAILS,
    title: HEADER_TITLES[HeaderKeys.HEADER_OPEX_DETAILS],
    meta: META_DATA[PortCoKeys.HEADER_OPEX_DETAILS]
  },
  [PortCoKeys.OPEX_SM]: {
    id: TableKeys.TOTAL_OPEX_SM,
    title: TABLE_TITLES[TableKeys.TOTAL_OPEX_SM],
    meta: META_DATA[PortCoKeys.OPEX_SM]
  },
  [PortCoKeys.OPEX_RD]: {
    id: TableKeys.TOTAL_OPEX_RD,
    title: TABLE_TITLES[TableKeys.TOTAL_OPEX_RD],
    meta: META_DATA[PortCoKeys.OPEX_RD]
  },
  [PortCoKeys.OPEX_GA]: {
    id: TableKeys.TOTAL_OPEX_GA,
    title: TABLE_TITLES[TableKeys.TOTAL_OPEX_GA],
    meta: META_DATA[PortCoKeys.OPEX_GA]
  },
  [PortCoKeys.OPEX_TOTAL]: {
    id: TableKeys.TOTAL_OPEX,
    title: TABLE_TITLES[TableKeys.TOTAL_OPEX],
    meta: META_DATA[PortCoKeys.OPEX_TOTAL]
  },
  [PortCoKeys.SEPARATOR_2]: {
    id: SeparatorKeys.SEPARATOR_2,
    title: '',
    meta: META_DATA[PortCoKeys.SEPARATOR_2]
  },
  [PortCoKeys.FCF]: {
    id: TableKeys.FCF,
    title: TABLE_TITLES[TableKeys.FCF],
    meta: META_DATA[PortCoKeys.FCF]
  },
  [PortCoKeys.MARGIN_FCF]: {
    id: TableKeys.FCF_MARGIN,
    title: TABLE_TITLES[TableKeys.FCF_MARGIN],
    meta: META_DATA[PortCoKeys.MARGIN_FCF]
  },
  [PortCoKeys.SEPARATOR_3]: {
    id: SeparatorKeys.SEPARATOR_3,
    title: '',
    meta: META_DATA[PortCoKeys.SEPARATOR_3]
  },
  [PortCoKeys.CUSTOMERS_EOP]: {
    id: TableKeys.TOTAL_CUSTOMERS,
    title: TABLE_TITLES[TableKeys.TOTAL_CUSTOMERS],
    meta: META_DATA[PortCoKeys.CUSTOMERS_EOP]
  },
  [PortCoKeys.CUSTOMERS_GROSSNEW]: {
    id: TableKeys.GROSS_NEW_CUSTOMERS_IN_PERIOD,
    title: TABLE_TITLES[TableKeys.GROSS_NEW_CUSTOMERS_IN_PERIOD],
    meta: META_DATA[PortCoKeys.CUSTOMERS_GROSSNEW]
  },
  [PortCoKeys.ARR_PER_CUSTOMER]: {
    id: TableKeys.ARR_PER_CUSTOMER,
    title: TABLE_TITLES[TableKeys.ARR_PER_CUSTOMER],
    meta: META_DATA[PortCoKeys.ARR_PER_CUSTOMER]
  },
  [PortCoKeys.SEPARATOR_4]: {
    id: SeparatorKeys.SEPARATOR_4,
    title: '',
    meta: META_DATA[PortCoKeys.SEPARATOR_4]
  },
  [PortCoKeys.HEADER_HEADCOUNT_DETAILS]: {
    id: HeaderKeys.HEADER_HEADCOUNT_DETAILS,
    title: HEADER_TITLES[HeaderKeys.HEADER_HEADCOUNT_DETAILS],
    meta: META_DATA[PortCoKeys.HEADER_HEADCOUNT_DETAILS]
  },
  [PortCoKeys.EMPLOYEES_SM]: {
    id: TableKeys.TOTAL_HEADCOUNT_SM,
    title: TABLE_TITLES[TableKeys.TOTAL_HEADCOUNT_SM],
    meta: META_DATA[PortCoKeys.EMPLOYEES_SM]
  },
  [PortCoKeys.EMPLOYEES_RD]: {
    id: TableKeys.TOTAL_HEADCOUNT_RD,
    title: TABLE_TITLES[TableKeys.TOTAL_HEADCOUNT_RD],
    meta: META_DATA[PortCoKeys.EMPLOYEES_RD]
  },
  [PortCoKeys.EMPLOYEES_GA]: {
    id: TableKeys.TOTAL_HEADCOUNT_GA,
    title: TABLE_TITLES[TableKeys.TOTAL_HEADCOUNT_GA],
    meta: META_DATA[PortCoKeys.EMPLOYEES_GA]
  },
  [PortCoKeys.EMPLOYEES_TOTAL]: {
    id: TableKeys.TOTAL_HEADCOUNT,
    title: TABLE_TITLES[TableKeys.TOTAL_HEADCOUNT],
    meta: META_DATA[PortCoKeys.EMPLOYEES_TOTAL]
  },
  [PortCoKeys.EMPLOYEES_OTHER]: {
    id: TableKeys.TOTAL_HEADCOUNT_OTHER,
    title: TABLE_TITLES[TableKeys.TOTAL_HEADCOUNT_OTHER],
    meta: META_DATA[PortCoKeys.EMPLOYEES_OTHER]
  },
  // ------------------------ GROWTH AND RETENTION ------------------------ //
  [PortCoKeys.YOY_ARR_GROWTH]: {
    id: TableKeys.YOY_ARR_GROWTH,
    title: TABLE_TITLES[TableKeys.YOY_ARR_GROWTH],
    meta: META_DATA[PortCoKeys.YOY_ARR_GROWTH]
  },
  [PortCoKeys.HEADER_GROSS_NEW_ARR_COMPOSITION]: {
    id: HeaderKeys.HEADER_GROSS_NEW_ARR_COMPOSITION,
    title: HEADER_TITLES[HeaderKeys.HEADER_GROSS_NEW_ARR_COMPOSITION],
    meta: META_DATA[PortCoKeys.HEADER_GROSS_NEW_ARR_COMPOSITION]
  },
  [PortCoKeys.ARR_PERCTOTAL_GROSSNEW_NEWLOGO]: {
    id: TableKeys.COMPOSITION_NEW_LOGO,
    title: TABLE_TITLES[TableKeys.COMPOSITION_NEW_LOGO],
    meta: META_DATA[PortCoKeys.ARR_PERCTOTAL_GROSSNEW_NEWLOGO]
  },
  [PortCoKeys.ARR_PERCTOTAL_GROSSNEW_EXPANSION]: {
    id: TableKeys.COMPOSITION_EXPANSION,
    title: TABLE_TITLES[TableKeys.COMPOSITION_EXPANSION],
    meta: META_DATA[PortCoKeys.ARR_PERCTOTAL_GROSSNEW_EXPANSION]
  },
  [PortCoKeys.NEW_LOGO_VELOCITY]: {
    id: TableKeys.NEW_LOGO_VELOCITY,
    title: TABLE_TITLES[TableKeys.NEW_LOGO_VELOCITY],
    meta: META_DATA[PortCoKeys.NEW_LOGO_VELOCITY]
  },
  [PortCoKeys.QUICK_RATIO_ARR]: {
    id: TableKeys.QUICK_RATIO,
    title: TABLE_TITLES[TableKeys.QUICK_RATIO],
    meta: META_DATA[PortCoKeys.QUICK_RATIO_ARR]
  },
  [PortCoKeys.RETENTION_GROSS_ARR_ANNUALIZED]: {
    id: TableKeys.GROSS_DOLLAR_RETENTION,
    title: TABLE_TITLES[TableKeys.GROSS_DOLLAR_RETENTION],
    meta: META_DATA[PortCoKeys.RETENTION_GROSS_ARR_ANNUALIZED]
  },
  [PortCoKeys.RETENTION_NET_ARR_ANNUALIZED]: {
    id: TableKeys.NET_DOLLAR_RETENTION,
    title: TABLE_TITLES[TableKeys.NET_DOLLAR_RETENTION],
    meta: META_DATA[PortCoKeys.RETENTION_NET_ARR_ANNUALIZED]
  },
  // ------------------------ OPERATIONAL EFFICIENCY ------------------------ //
  [PortCoKeys.MAGICNUMBER_NET]: {
    id: TableKeys.NET_MAGIC_NUMBER,
    title: TABLE_TITLES[TableKeys.NET_MAGIC_NUMBER],
    meta: META_DATA[PortCoKeys.MAGICNUMBER_NET]
  },
  [PortCoKeys.RULE_OF_40]: {
    id: TableKeys.RULE_OF_40,
    title: TABLE_TITLES[TableKeys.RULE_OF_40],
    meta: META_DATA[PortCoKeys.RULE_OF_40]
  },
  [PortCoKeys.CAC]: {
    id: TableKeys.CAC,
    title: TABLE_TITLES[TableKeys.CAC],
    meta: META_DATA[PortCoKeys.CAC]
  },
  [PortCoKeys.CAC_PAYBACK]: {
    id: TableKeys.CAC_PAYBACK_PERIOD,
    title: TABLE_TITLES[TableKeys.CAC_PAYBACK_PERIOD],
    meta: META_DATA[PortCoKeys.CAC_PAYBACK]
  },
  [PortCoKeys.BURN_MULTIPLE]: {
    id: TableKeys.BURN_MULTIPLE,
    title: TABLE_TITLES[TableKeys.BURN_MULTIPLE],
    meta: META_DATA[PortCoKeys.BURN_MULTIPLE]
  },
  [PortCoKeys.HEADER_OPEX_AS_PERC_OF_REVENUE]: {
    id: HeaderKeys.HEADER_OPEX_AS_PERC_OF_REVENUE,
    title: HEADER_TITLES[HeaderKeys.HEADER_OPEX_AS_PERC_OF_REVENUE],
    meta: META_DATA[PortCoKeys.HEADER_OPEX_AS_PERC_OF_REVENUE]
  },
  [PortCoKeys.MARGIN_OPEX_SM]: {
    id: TableKeys.PERC_OPEX_SM,
    title: TABLE_TITLES[TableKeys.PERC_OPEX_SM],
    meta: META_DATA[PortCoKeys.MARGIN_OPEX_SM]
  },
  [PortCoKeys.MARGIN_OPEX_RD]: {
    id: TableKeys.PERC_OPEX_RD,
    title: TABLE_TITLES[TableKeys.PERC_OPEX_RD],
    meta: META_DATA[PortCoKeys.MARGIN_OPEX_RD]
  },
  [PortCoKeys.MARGIN_OPEX_GA]: {
    id: TableKeys.PERC_OPEX_GA,
    title: TABLE_TITLES[TableKeys.PERC_OPEX_GA],
    meta: META_DATA[PortCoKeys.MARGIN_OPEX_GA]
  },
  // ------------------------ PROFITABILITY AND CASH FLOW ------------------------ //
  [PortCoKeys['MARGIN_GROSSPROFIT.1']]: {
    id: TableKeys.GROSS_PROFIT_MARGIN_1,
    title: TABLE_TITLES[TableKeys.GROSS_PROFIT_MARGIN_1],
    meta: META_DATA[PortCoKeys['MARGIN_GROSSPROFIT.1']]
  },
  [PortCoKeys['MARGIN_FCF.1']]: {
    id: TableKeys.FCF_MARGIN_1,
    title: TABLE_TITLES[TableKeys.FCF_MARGIN_1],
    meta: META_DATA[PortCoKeys['MARGIN_FCF.1']]
  },
  // ------------------------ HEADCOUNT ------------------------ //
  [PortCoKeys.ARR_PER_FTE]: {
    id: TableKeys.ARR_PER_FTE,
    title: TABLE_TITLES[TableKeys.ARR_PER_FTE],
    meta: META_DATA[PortCoKeys.ARR_PER_FTE]
  },
  [PortCoKeys.OPEX_PER_FTE]: {
    id: TableKeys.OPEX_PER_FTE,
    title: TABLE_TITLES[TableKeys.OPEX_PER_FTE],
    meta: META_DATA[PortCoKeys.OPEX_PER_FTE]
  },
  [PortCoKeys.HEADER_HEADCOUNT_DISTRIBUTION]: {
    id: HeaderKeys.HEADER_HEADCOUNT_DISTRIBUTION,
    title: HEADER_TITLES[HeaderKeys.HEADER_HEADCOUNT_DISTRIBUTION],
    meta: META_DATA[PortCoKeys.HEADER_HEADCOUNT_DISTRIBUTION]
  },
  [PortCoKeys.EMPLOYEES_PERCTOTAL_SM]: {
    id: TableKeys.PERC_HEADCOUNT_SM,
    title: TABLE_TITLES[TableKeys.PERC_HEADCOUNT_SM],
    meta: META_DATA[PortCoKeys.EMPLOYEES_PERCTOTAL_SM]
  },
  [PortCoKeys.EMPLOYEES_PERCTOTAL_RD]: {
    id: TableKeys.PERC_HEADCOUNT_RD,
    title: TABLE_TITLES[TableKeys.PERC_HEADCOUNT_RD],
    meta: META_DATA[PortCoKeys.EMPLOYEES_PERCTOTAL_RD]
  },
  [PortCoKeys.EMPLOYEES_PERCTOTAL_GA]: {
    id: TableKeys.PERC_HEADCOUNT_GA,
    title: TABLE_TITLES[TableKeys.PERC_HEADCOUNT_GA],
    meta: META_DATA[PortCoKeys.EMPLOYEES_PERCTOTAL_GA]
  },
  [PortCoKeys.EMPLOYEES_PERCTOTAL_OTHER]: {
    id: TableKeys.PERC_HEADCOUNT_OTHER,
    title: TABLE_TITLES[TableKeys.PERC_HEADCOUNT_OTHER],
    meta: META_DATA[PortCoKeys.EMPLOYEES_PERCTOTAL_OTHER]
  }
};

export const SELECT_CLASSNAMES = {
  input: () => 'hidden',
  menu: () => 'min-w-48 -ml-8',
  menuList: () => 'dark:text-black',
  loadingIndicator: () => '!hidden',
  dropdownIndicator: () => '!hidden',
  indicatorSeparator: () => '!hidden',
  placeholder: () => '!text-[#151515] text-center',
  control: () =>
    'min-w-24 h-10 !rounded-5xl !cursor-pointer walkthrough-history-data'
};

export const DATE_OPTIONS = {
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric'
} as const;
